import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowRight, Bell, CreditCard, Megaphone, Sparkles, X } from 'lucide-react';
import type { UserNotification, UserNotificationType } from '../lib/notificationTypes';
import { cn, safeParseDate } from '../lib/utils';

type InAppNotificationBannerProps = {
  notification: UserNotification | null;
  onDismiss: () => void;
  onOpenLink?: (href: string) => void;
};

function typeMeta(type: UserNotificationType) {
  switch (type) {
    case 'billing':
      return {
        label: 'Billing',
        icon: CreditCard,
        accent: 'from-rose-500/20 via-rose-400/10 to-transparent',
        ring: 'ring-rose-200/60',
        iconBg: 'bg-rose-50 text-rose-600',
        pill: 'bg-rose-50 text-rose-700',
      };
    case 'feature':
      return {
        label: 'New feature',
        icon: Sparkles,
        accent: 'from-violet-500/20 via-indigo-400/10 to-transparent',
        ring: 'ring-violet-200/60',
        iconBg: 'bg-violet-50 text-violet-600',
        pill: 'bg-violet-50 text-violet-700',
      };
    case 'campaign':
      return {
        label: 'Update',
        icon: Megaphone,
        accent: 'from-sky-500/20 via-blue-400/10 to-transparent',
        ring: 'ring-sky-200/60',
        iconBg: 'bg-sky-50 text-sky-600',
        pill: 'bg-sky-50 text-sky-700',
      };
    default:
      return {
        label: 'Announcement',
        icon: Bell,
        accent: 'from-indigo-500/20 via-slate-400/10 to-transparent',
        ring: 'ring-indigo-200/60',
        iconBg: 'bg-indigo-50 text-indigo-600',
        pill: 'bg-indigo-50 text-indigo-700',
      };
  }
}

export default function InAppNotificationBanner({
  notification,
  onDismiss,
  onOpenLink,
}: InAppNotificationBannerProps) {
  useEffect(() => {
    if (!notification) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [notification, onDismiss]);

  if (typeof document === 'undefined') return null;

  const meta = notification ? typeMeta(notification.type) : null;
  const Icon = meta?.icon ?? Bell;
  const createdLabel = notification
    ? safeParseDate(notification.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return createPortal(
    <AnimatePresence>
      {notification && meta ? (
        <motion.div
          key={notification.id}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            aria-label="Close notification"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-[6px]"
            onClick={onDismiss}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="in-app-notification-title"
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className={cn(
              'relative w-full max-w-lg overflow-hidden rounded-[1.75rem]',
              'border border-white/80 bg-white/95 shadow-[0_24px_80px_-20px_rgba(15,23,42,0.45)]',
              'backdrop-blur-xl ring-1',
              meta.ring,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={cn(
                'pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b',
                meta.accent,
              )}
              aria-hidden
            />

            <div className="relative px-6 pt-6 pb-5 sm:px-7 sm:pt-7">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-2xl shadow-sm',
                      meta.iconBg,
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em]',
                        meta.pill,
                      )}
                    >
                      {meta.label}
                    </span>
                    <p className="mt-1 text-[11px] font-medium text-slate-400">{createdLabel}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h2
                id="in-app-notification-title"
                className="mt-5 text-[1.35rem] font-semibold leading-snug tracking-tight text-slate-900 sm:text-2xl"
              >
                {notification.title}
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600 whitespace-pre-wrap">
                {notification.body}
              </p>
            </div>

            <div className="relative flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:px-7">
              <button
                type="button"
                onClick={onDismiss}
                className="rounded-full px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-white hover:text-slate-700"
              >
                Got it
              </button>
              {notification.href && onOpenLink ? (
                <button
                  type="button"
                  onClick={() => onOpenLink(notification.href!)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
                >
                  Open
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
